import { AUTH_METHODS, ROLES, STAFF_ROLES, type AuthMethod, type Role } from './enums';

export const PERMISSIONS = [
  'households.read',
  'households.write',
  'households.chat',
  'decisions.write',
  'scores.override',
  'scores.feedback',
  'scores.rescore',
  'scores.feedback.read',
  'recommendations.write',
  'recommendations.import',
  'activities.write',
  'duplicates.resolve',
  'ingestion.read',
  'audit.read',
  'settings.read',
  'settings.write',
  'prompts.scoring',
  'prompts.chat',
  'assistant.feedback.read',
  'users.manage',
  'referrals.configure',
  'referrals.review',
  'referrals.own',
  'notifications.manage',
  'access_requests.manage',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

const TRUSTEE_PERMISSIONS: readonly Permission[] = ['referrals.own'];

const ADMISSIONS_PERMISSIONS: readonly Permission[] = [
  'households.read',
  'households.write',
  'decisions.write',
  'scores.feedback',
  'scores.rescore',
  'recommendations.write',
  'recommendations.import',
  'activities.write',
  'duplicates.resolve',
  'ingestion.read',
  'referrals.review',
];

export const ROLE_PERMISSIONS: Record<Role, readonly Permission[]> = {
  super_admin: PERMISSIONS.filter((p) => !TRUSTEE_PERMISSIONS.includes(p)),
  admissions_admin: [
    ...ADMISSIONS_PERMISSIONS,
    'households.chat',
    'scores.override',
    'prompts.scoring',
    'prompts.chat',
    'assistant.feedback.read',
    'scores.feedback.read',
  ],
  admissions: ADMISSIONS_PERMISSIONS,
  trustee: TRUSTEE_PERMISSIONS,
};

export function hasPermission(roles: readonly string[], permission: Permission): boolean {
  return roles.some((role) => ROLE_PERMISSIONS[role as Role]?.includes(permission) ?? false);
}

export function permissionsFor(roles: readonly string[]): Permission[] {
  return PERMISSIONS.filter((p) => hasPermission(roles, p));
}

export function isRole(value: string): value is Role {
  return (ROLES as readonly string[]).includes(value);
}

export function parseRoles(values: readonly string[]): { roles: Role[]; unknown: string[] } {
  const known = new Set(values.filter(isRole));
  return { roles: ROLES.filter((r) => known.has(r)), unknown: values.filter((v) => !isRole(v)) };
}

export function hasStaffRole(roles: readonly string[]): boolean {
  return roles.some((r) => STAFF_ROLES.includes(r as Role));
}

export function staffRoleOf(roles: readonly string[]): Role | null {
  return ROLES.find((r) => STAFF_ROLES.includes(r) && roles.includes(r)) ?? null;
}

export function composeRoles(staffRole: Role | null, trustee: boolean): Role[] {
  return parseRoles([...(staffRole ? [staffRole] : []), ...(trustee ? ['trustee'] : [])]).roles;
}

export function mergeRoles(existing: readonly string[], added: readonly string[]): Role[] {
  return composeRoles(staffRoleOf(added) ?? staffRoleOf(existing), existing.includes('trustee') || added.includes('trustee'));
}

export function defaultAuthMethods(roles: readonly string[]): AuthMethod[] {
  return hasStaffRole(roles) ? ['google'] : ['google', 'email_code'];
}

export function parseAuthMethods(values: readonly string[]): AuthMethod[] {
  return AUTH_METHODS.filter((m) => values.includes(m));
}
